import Vue from 'vue';
import VeeValidate, { Validator } from 'vee-validate';
import { default as campaignsStore } from 'Stores/modules/campaign/store';
import {
    forEach as _forEach,
    forOwn as _forOwn,
    join as _join,
    size as _size,
    find as _find,
    isEmpty as _isEmpty,
    indexOf as _indexOf
} from 'lodash';
import * as VALIDATION_RULES from "Constants/emailer/validationRules";
import * as VALIDATION_RULES_TARGETING from "../constants/campaign/targeting/validationRules";

/**
 * library class for Validation using Vue
 *
 */
export default class VueValidatorHelper
{
    /**
     * Set rules for the validator to use
     */
    attachRules()
    {
        const self = this;
        _forEach(self.fields, function(field, key) {
            if(!_isEmpty(field.validationRules)){
                const fieldRules = [];
                _forOwn(field.validationRules, function(message, rule) {
                    if(typeof message === 'string') {
                        fieldRules.push(rule);
                    }
                });
                if(_size(fieldRules)) {
                    // Add a 'rules' property to the field to make sure the rules passed on match Laravel's convention
                    field.rules = _join(fieldRules, "|");
                    self.validator.attach(field.name, field.rules);
                }
            }
        });
    }

    /**
     *
     * @param fields
     */
    constructor(fields)
    {
        this.setFormFields(fields);
    }

    /**
     *
     * @returns {}
     **/
    getFields()
    {
        return this.fields;
    }

    /**
     * Returns the Validator currently used
     * @returns {{}}
     **/
    getValidator()
    {
        return this.validator;
    }

    /**
     * validates a field.
     * Note: the validator must have the fields attached to it already
     *
     * @param fieldName
     * @param value
     */
    isFieldValid(fieldName, value)
    {
        const self = this;
        let errorMessage = '';

        if(_isEmpty(self.validator)){
            return Promise.reject('Missing validator, the validator must be set on the validator helper');
        }

        if(_isEmpty(_find(self.validator.fields.items, ['name', fieldName]))){
            // If the field is not in the validator fields to validate,
            // the field is valid by default.
            // Note: Make sure fields rules are properly set so they are attached to the validator
            return Promise.resolve( { isValid: true, message: ''});
        }

        // TODO:: check (& set) validation results from the state
        return self.validator.validate(fieldName, value)
            .then((isValid) => {
                if(!isValid){
                  errorMessage = self.getValidationErrorMessage(fieldName);
                  return Promise.resolve({isValid, message: errorMessage});
                }
                return Promise.resolve({isValid: true, message: ''});
            }).catch((error) => {
                return Promise.resolve({ isValid: false, message: error});
            });
    }

    /**
     * Get the Validation Error Message for the given field
     * @param fieldName
     * @returns {string}
     */
    getValidationErrorMessage(fieldName)
    {
        const self = this;
        let errorMessage =  '';
        const field = _find(self.fields, ['name', fieldName]);

        if(!_isEmpty(field) && !_isEmpty(field.validationRules)){
            const errors = self.validator.errors || {};
            const fieldError = _find(errors.items, ['field', fieldName]) || {};

            _forOwn(field.validationRules, function(message, rule){
                if(rule.indexOf(fieldError.rule) !== -1){
                    errorMessage = message;
                }
            });
        }
        return errorMessage;
    }

    /**
     * Set the store's form Fields
     * @param fields
     **/
    setFormFields(fields)
    {
        this.fields = fields;
    }

    /**
     *
     * @param validator
     */
    setValidator(validator)
    {
        this.validator = validator;
        this.attachRules();
    }

    /**
     * Get validation message for invalid from name
     * should return false if username is invalid
     */
    static isValidEmailFromName() {
        return {
            getMessage: (field, args) => {
                return VALIDATION_RULES.INVALID_EMAIL_FROM_NAME;
            },
            validate: (value, args) => {
                const excludedList = VALIDATION_RULES.INVALID_NAME_CHARACTERS;
                return !this.containsSpecialCharacter(value,excludedList);
            }
        }
    }

    /**
     * Get validation message for invalid reply to name
     * should return false if username is invalid
     */
    static isValidEmailReplyToName() {
        return {
            getMessage: (field, args) => {
                return VALIDATION_RULES.INVALID_EMAIL_REPLY_TO_NAME;
            },
            validate: (value, args) => {
                const excludedList = VALIDATION_RULES.INVALID_NAME_CHARACTERS;
                return !this.containsSpecialCharacter(value,excludedList);
            }
        }
    }

    /**
     * Get validation message for invalid username
     * should return false if username is invalid
     */
    static isValidEmailUsername() {
       return {
            getMessage: (field, args) => {
                return VALIDATION_RULES.INVALID_EMAIL_USERNAME;
            },
            validate: (value, args) => {
               const excludedList = VALIDATION_RULES.INVALID_SPECIAL_CHARACTERS;
               return !this.containsSpecialCharacter(value,excludedList);
            }
        }
    }

    /**
     *
     * @param value
     * @param excludedList
     * @returns {boolean}
     * check whether the username has an invalid special character
     */
    static containsSpecialCharacter(value, excludedList) {
        let hasSpecialCharacters = false;
        _find(excludedList,function(splChar){
           if(value.indexOf(splChar) != -1) {
               hasSpecialCharacters = true;
           }
        });
        return hasSpecialCharacters;
    }

    /**
     * Get valid domain name for emails
     */
    static isValidDomain() {
        return {
            getMessage: (field,args) => {
                return VALIDATION_RULES.INVALID_DOMAIN_MESSAGE;
            },
            validate: (value,args) => {
                return this.checkDomainValidity(value);
            }
        }
    }

    /**
     *
     * @param value
     * @returns {boolean}
     */
    static checkDomainValidity(value){
        const match = value.match(/[a-zA-Z0-9]{1,60}[a-zA-Z0-9-]*(?:\.[a-zA-Z]{2,})+/);
        return !_isEmpty(match) && match[0] === value;
    }

    static isValidTwitterUserName() {
        return {
            getMessage: (field,args) => {
                return VALIDATION_RULES_TARGETING.INVALID_TWITTER_USERNAME_MESSAGE;
            },
            validate: (value,args) => {
                return this.checkTwitterUserName(value);
            }
        }
    }

    static checkTwitterUserName(value) {
        const forbiddenWords = ['admin', 'twitter'];
        const username = new RegExp(/^@?\w{1,15}$/);
        let hasForbiddenWords = true;

        _find(forbiddenWords, word => {
            if (value.indexOf(word) != -1) {
                hasForbiddenWords = false;
            }
        });

        return username.test(value) && hasForbiddenWords;
    }
};
